/**
 * A BU's group of cards, behind a collapsible header.
 *
 * Used by Páginas and Tours, which both list per business unit: an icon, the tenant slug in mono,
 * a count, and a right-aligned summary. The open/closed state belongs to the caller, so it
 * survives re-renders and can be seeded per BU.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import type { IGLIcon } from '@group-link-one/gl-icons';
import type { JSX, ReactNode } from 'react';

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
`;

const headerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: 0 0 var(--space-s);
  border: none;
  border-bottom: 1px solid var(--color-divider);
  background: transparent;
  width: 100%;
  cursor: pointer;
  text-align: left;
  font-family: var(--font-sans);
`;

const iconBoxStyle = css`
  width: 28px;
  height: 28px;
  flex: 0 0 auto;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-surface-sunken);
`;

const titleStyle = css`
  font-size: 14px;
  font-weight: var(--font-weight-bold);
  color: var(--color-text);
`;

/**
 * Monospace, for a title that is an identifier.
 *
 * The BU sections are headed by a tenant slug, which is a code and reads as one. A section headed
 * by a sentence is prose and must not borrow the face: monospace is how this panel says "this
 * string is typed, not written".
 */
const monoTitleStyle = css`
  font-family: var(--font-mono);
`;

const countStyle = css`
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;

const spacerStyle = css`
  flex: 1;
`;

const summaryStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;

export interface CollapsibleSectionProps {
  /** The section's heading. */
  title: string;
  /** Set when the title is an identifier — a tenant slug — rather than a sentence. */
  monoTitle?: boolean;
  /** Tenant icon. */
  icon: IGLIcon['variant'];
  /** Color for the icon, from the tenant's domain. */
  iconColor?: string;
  /** e.g. "3 páginas". */
  count: string;
  /** Right-aligned summary, e.g. "2 ativas". */
  summary?: string;
  open: boolean;
  /**
   * Keep the children in the tree while collapsed, hidden with `display: none`.
   *
   * For a **form**, unmounting is a risk not worth taking: anything a field holds that the form
   * state does not — a selection, an in-progress IME composition, the scroll inside a textarea —
   * disappears with it, silently, which is the worst way for work to be lost. Hidden content is
   * also not focusable, so a collapsed step stays out of the tab order.
   *
   * Off by default: a listing has nothing to lose and is cheaper not rendering.
   */
  keepMounted?: boolean;
  onToggle: () => void;
  children: ReactNode;
  id?: string;
}

export const CollapsibleSection = ({
  title,
  monoTitle = false,
  keepMounted = false,
  icon,
  iconColor = 'var(--color-text-secondary)',
  count,
  summary,
  open,
  onToggle,
  children,
  id,
}: CollapsibleSectionProps): JSX.Element => (
  <section className={sectionStyle}>
    <button
      id={id}
      type='button'
      onClick={onToggle}
      aria-expanded={open}
      aria-label={`${open ? 'Recolher' : 'Expandir'} ${title}`}
      className={headerStyle}
    >
      <GLIcon
        variant={open ? 'Arrows.CaretDown' : 'Arrows.CaretRight'}
        size={14}
        fill='var(--color-text-muted)'
      />
      <span className={iconBoxStyle}>
        <GLIcon variant={icon} size={16} fill={iconColor} />
      </span>
      <span className={`${titleStyle} ${monoTitle ? monoTitleStyle : ''}`}>{title}</span>
      <span className={countStyle}>{count}</span>
      <div className={spacerStyle} />
      {summary ? <span className={summaryStyle}>{summary}</span> : null}
    </button>

    {keepMounted ? (
      <div style={{ display: open ? undefined : 'none' }}>{children}</div>
    ) : open ? (
      children
    ) : null}
  </section>
);
